'use client';

import { useEffect, useRef, useState } from 'react';
import { PromptBuild } from '@/lib/types';
import api from '@/lib/api';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';

interface BuildsMenuProps {
  prompt: string;
  onLoad: (prompt: string) => void;
}

// Compact relative time for the build rows (from updated_at)
function relativeTime(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

/**
 * Compact "Builds ▾" dropdown for saving / loading named prompt builds.
 * The list is fetched lazily on first open and refreshed after every mutation.
 */
export default function BuildsMenu({ prompt, onLoad }: BuildsMenuProps) {
  const [open, setOpen] = useState(false);
  // null = not fetched yet (lazy fetch on first open)
  const [builds, setBuilds] = useState<PromptBuild[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Build id with an overwrite/delete in flight (serializes row mutations)
  const [busyId, setBusyId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  // Close on outside click (same pattern as SectionRibbon's + Add menu)
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const fetchBuilds = async () => {
    setIsLoading(true);
    try {
      setBuilds(await api.getPromptBuilds());
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && builds === null && !isLoading) fetchBuilds();
  };

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name || !prompt.trim() || isSaving) return;
    setIsSaving(true);
    try {
      await api.createPromptBuild({ name, prompt });
      toast.success(`Saved build "${name}"`);
      setSaveName('');
      await fetchBuilds();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoad = async (build: PromptBuild) => {
    if (prompt.trim() && prompt !== build.prompt) {
      const confirmed = await confirm({
        title: 'Load Build',
        message: `Replace the current prompt with "${build.name}"? Unsaved changes will be lost.`,
        confirmText: 'Load',
        variant: 'warning',
      });
      if (!confirmed) return;
    }
    onLoad(build.prompt);
    setOpen(false);
  };

  const handleOverwrite = async (build: PromptBuild) => {
    if (!prompt.trim() || busyId !== null) return;
    setBusyId(build.id);
    try {
      await api.updatePromptBuild(build.id, { prompt });
      toast.success(`Overwrote "${build.name}" with the current prompt`);
      await fetchBuilds();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (build: PromptBuild) => {
    if (busyId !== null) return;
    const confirmed = await confirm({
      title: 'Delete Build',
      message: `Delete "${build.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    setBusyId(build.id);
    try {
      await api.deletePromptBuild(build.id);
      toast.success('Build deleted');
      await fetchBuilds();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const canOverwrite = prompt.trim().length > 0 && busyId === null;

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={`px-3 h-10 rounded-lg text-sm font-medium border transition-colors cursor-pointer ${
          open
            ? 'bg-[var(--accent-soft)] text-[var(--accent)] border-[var(--accent)]'
            : 'text-[var(--text-muted)] border-[var(--border-subtle)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
        }`}
        title="Saved builds"
      >
        Builds ▾
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-80 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] shadow-xl overflow-hidden">
          {/* Save current */}
          <div className="p-2 border-b border-[var(--border-subtle)] flex items-center gap-2">
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
              className="input text-sm flex-1 min-w-0 py-1.5"
              placeholder="Save current as…"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={!prompt.trim() || !saveName.trim() || isSaving}
              className="btn btn-primary text-xs py-1.5 px-3 shrink-0"
              title={prompt.trim() ? 'Save the current prompt as a build' : 'Prompt is empty'}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>

          {/* Build list */}
          <div className="max-h-72 overflow-y-auto">
            {isLoading && builds === null ? (
              <div className="px-3 py-4 text-xs text-[var(--text-muted)] text-center">Loading…</div>
            ) : !builds || builds.length === 0 ? (
              <div className="px-3 py-4 text-xs text-[var(--text-muted)] text-center">No saved builds yet</div>
            ) : (
              builds.map((build) => (
                <div
                  key={build.id}
                  className="group flex items-center gap-1 px-2 py-1.5 hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => handleLoad(build)}
                    className="flex-1 min-w-0 text-left cursor-pointer"
                    title={`Load "${build.name}"`}
                  >
                    <span className="flex items-center gap-1.5">
                      <span className="text-sm truncate">{build.name}</span>
                      {build.is_private && (
                        <svg
                          className="w-3 h-3 shrink-0 text-[var(--text-muted)]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <title>Private</title>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                      )}
                    </span>
                    <span className="block text-[10px] text-[var(--text-muted)]">
                      {relativeTime(build.updated_at)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOverwrite(build)}
                    disabled={!canOverwrite}
                    className={`p-1 rounded transition-colors shrink-0 opacity-0 group-hover:opacity-100 ${
                      canOverwrite
                        ? 'text-[var(--text-muted)] hover:text-[var(--accent)] cursor-pointer'
                        : 'text-[var(--text-muted)]/40 cursor-not-allowed'
                    }`}
                    title="Overwrite with current prompt"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M12 2.25v10.5m0 0l-3-3m3 3l3-3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(build)}
                    disabled={busyId !== null}
                    className={`px-1 rounded transition-colors shrink-0 opacity-0 group-hover:opacity-100 ${
                      busyId === null
                        ? 'text-[var(--text-muted)] hover:text-[var(--error)] cursor-pointer'
                        : 'text-[var(--text-muted)]/40 cursor-not-allowed'
                    }`}
                    title={`Delete "${build.name}"`}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
